import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, RefreshCw, Shield } from 'lucide-react';
import TeamMemberModal from './TeamMemberModal';
import { useToast } from '../../hooks/use-toast';

const TeamTab = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  useEffect(() => {
    fetchCurrentAdmin();
    fetchTeamMembers();
  }, []);

  const fetchCurrentAdmin = async () => {
    try {
      // Check admin token first (for admin OTP login)
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        const response = await fetch(`${API_BASE_URL}/api/admin/me`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCurrentAdmin({
            is_admin: true,
            admin_role: data.role,
            email: data.email,
            username: data.username
          });
          return;
        }
      }
      
      // Fallback to regular auth token
      const authToken = localStorage.getItem('auth_token');
      if (authToken) {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCurrentAdmin(data);
        }
      }
    } catch (error) {
      console.error('Error fetching current admin:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      console.log('🔑 Admin token for team:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('📥 Team response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Team data:', data);
        setTeamMembers(data.team_members);
      } else {
        const error = await response.text();
        console.error('❌ Team error:', error);
      }
    } catch (error) {
      console.error('❌ Team fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/team/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: "✅ Team Member Deleted",
          description: 'Team member removed successfully.',
          duration: 3000,
        });
        fetchTeamMembers();
      } else {
        const error = await response.json();
        toast({
          title: "Delete Failed",
          description: error.detail || 'Failed to delete team member',
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast({
        title: "Delete Error",
        description: 'Failed to delete team member. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      'super_admin': 'from-purple-500 to-pink-600',
      'admin': 'from-blue-400 to-indigo-500',
      'support': 'from-green-400 to-emerald-500',
      'developer': 'from-orange-400 to-yellow-500'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${colors[role] || 'from-gray-400 to-gray-500'} text-white`}>
        {role?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const isSuperAdmin = currentAdmin?.is_admin && currentAdmin?.admin_role === 'super_admin';

  return (
    <div>
      {/* Header with Create Button */}
      {isSuperAdmin && (
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-400 to-violet-500 text-white font-medium hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            Add Team Member
          </button>
        </div>
      )}

      {/* Team Members Table */}
      <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-purple-400 animate-spin mb-4" />
            <p className="text-gray-600">Loading team members...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-violet-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  {isSuperAdmin && (
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-purple-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">{member.name || member.username}</div>
                        {member.role === 'super_admin' && (
                          <Shield className="w-4 h-4 text-purple-500" title="Super Admin" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{member.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(member.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${member.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingMember(member)}
                            className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-purple-500" />
                          </button>
                          {/* Cannot delete yourself OR the primary super admin (owner) */}
                          {!member.is_primary && member.id !== currentAdmin?.id && (
                            <button
                              onClick={() => handleDeleteMember(member.id)}
                              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                          {member.is_primary && (
                            <span className="px-3 py-1 text-xs text-gray-500 font-medium">
                              Owner (Protected)
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingMember) && (
        <TeamMemberModal
          member={editingMember}
          onClose={() => {
            setShowCreateModal(false);
            setEditingMember(null);
            fetchTeamMembers();
          }}
        />
      )}
    </div>
  );
};

export default TeamTab;